/**
 * PlatformAnnouncementBanner
 * Renders a compact inline banner at the TOP of the main content area
 * showing platform-level broadcasts sent by the Super Admin.
 *
 * Behaviour:
 *  - Collapsed by default (just a slim header bar with count badge)
 *  - Click header to expand and read announcements
 *  - Once expanded (seen), the banner hides itself entirely until new
 *    broadcasts arrive that haven't been seen yet
 *  - Individual announcements can also be dismissed (X button) — persists across navigation
 *
 * Props:
 *   audience  — 'admins' | 'teachers' | 'parents' | 'students'
 *   schoolId  — the school's _id string
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Megaphone, X, ChevronDown, ChevronUp } from 'lucide-react';

import { SERVER_BASE } from '../api/client';
const API_BASE = SERVER_BASE + '/api/superadmin';
const LS_DISMISSED = 'pa_dismissed_v2';
const LS_SEEN      = 'pa_seen_v2';

const TYPE_CONFIG = {
  info:        { color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE', icon: 'ℹ️',  label: 'Info'        },
  warning:     { color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', icon: '⚠️',  label: 'Warning'     },
  maintenance: { color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE', icon: '🔧',  label: 'Maintenance' },
  feature:     { color: '#059669', bg: '#ECFDF5', border: '#A7F3D0', icon: '✨',  label: 'New Feature' },
  urgent:      { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', icon: '🚨',  label: 'Urgent'      },
  critical:    { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', icon: '🔴',  label: 'Critical'    },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
}

function readLS(key) {
  try { return JSON.parse(localStorage.getItem(key) || '[]').map(String); } catch { return []; }
}
function writeLS(key, arr) {
  try { localStorage.setItem(key, JSON.stringify(arr)); } catch {}
}

export default function PlatformAnnouncementBanner({ audience = 'admins', schoolId }) {
  const [broadcasts, setBroadcasts] = useState([]);
  // Use plain arrays — React detects new array references as state changes
  const [dismissed, setDismissed] = useState(() => readLS(LS_DISMISSED));
  const [seen,      setSeen]      = useState(() => readLS(LS_SEEN));
  const [expanded,  setExpanded]  = useState(false);

  const fetchBroadcasts = useCallback(() => {
    if (!schoolId) return;
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/broadcasts/for-school/${schoolId}?audience=${audience}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setBroadcasts(data); })
      .catch(() => {});
  }, [schoolId, audience]);

  useEffect(() => {
    fetchBroadcasts();
    const interval = setInterval(fetchBroadcasts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchBroadcasts]);

  // Dismiss a single broadcast — persists to localStorage immediately
  const dismissBroadcast = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const sid = String(id);
    if (dismissed.includes(sid)) return; // already dismissed
    const nextDismissed = [...dismissed, sid];
    const nextSeen      = seen.includes(sid) ? seen : [...seen, sid];
    setDismissed(nextDismissed);
    setSeen(nextSeen);
    writeLS(LS_DISMISSED, nextDismissed);
    writeLS(LS_SEEN, nextSeen);
    // Fire-and-forget read receipt
    if (schoolId) {
      const token = localStorage.getItem('token');
      fetch(`${API_BASE}/broadcasts/${sid}/read`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ school_id: schoolId }),
      }).catch(() => {});
    }
  };

  // Broadcasts that haven't been dismissed
  const notDismissed = broadcasts.filter(b => !dismissed.includes(String(b._id)));
  // Broadcasts that are new (not yet seen and not dismissed)
  const unseen = notDismissed.filter(b => !seen.includes(String(b._id)));

  const handleToggle = () => {
    if (!expanded) {
      // Mark all visible as seen when opening
      const newIds = notDismissed.map(b => String(b._id)).filter(id => !seen.includes(id));
      if (newIds.length > 0) {
        const nextSeen = [...seen, ...newIds];
        setSeen(nextSeen);
        writeLS(LS_SEEN, nextSeen);
      }
    }
    setExpanded(e => !e);
  };

  // Only show the banner if there are unseen broadcasts (or it's currently expanded)
  if (unseen.length === 0 && !expanded) return null;
  if (notDismissed.length === 0) return null;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E5E7EB',
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      flexShrink: 0,
    }}>
      {/* Header row — click to toggle */}
      <button
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '7px 14px',
          background: '#F9FAFB',
          width: '100%',
          border: 'none',
          borderBottom: expanded ? '1px solid #F3F4F6' : 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Megaphone size={13} color="#6366F1" />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: 0.2 }}>
            Platform Announcements
          </span>
          {unseen.length > 0 && (
            <span style={{
              background: '#EF4444', color: '#fff',
              fontSize: 10, fontWeight: 800,
              borderRadius: 8, padding: '1px 6px', lineHeight: '14px',
            }}>
              {unseen.length} new
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7280', fontWeight: 500 }}>
          {expanded
            ? <><ChevronUp size={13} /> <span>Hide</span></>
            : <><ChevronDown size={13} /> <span>Show</span></>
          }
        </div>
      </button>

      {/* Announcement rows — only shown when expanded */}
      {expanded && notDismissed.map((b, idx) => {
        const cfg = TYPE_CONFIG[b.type] || TYPE_CONFIG.info;
        const isLast = idx === notDismissed.length - 1;
        const isUnseen = !seen.includes(String(b._id));
        return (
          <div
            key={b._id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 14px',
              background: cfg.bg,
              borderBottom: isLast ? 'none' : `1px solid ${cfg.border}`,
              borderLeft: `3px solid ${cfg.color}`,
            }}
          >
            <span style={{ fontSize: 16, flexShrink: 0 }}>{cfg.icon}</span>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{b.title}</span>
                <span style={{
                  fontSize: 10, fontWeight: 600,
                  color: cfg.color, background: `${cfg.color}18`,
                  borderRadius: 4, padding: '1px 5px', textTransform: 'capitalize',
                }}>
                  {cfg.label}
                </span>
                {isUnseen && (
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: cfg.color, display: 'inline-block', flexShrink: 0,
                  }} />
                )}
              </div>
              {b.message && (
                <div style={{
                  fontSize: 12, color: '#4B5563', marginTop: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  maxWidth: '80vw',
                }}>
                  {b.message}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                {formatDate(b.createdAt)}
                {b.expires_at && ` · Expires ${formatDate(b.expires_at)}`}
              </div>
            </div>

            {/* Dismiss X button */}
            <button
              onClick={(e) => dismissBroadcast(e, b._id)}
              title="Dismiss"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px', borderRadius: 5, color: '#9CA3AF',
                display: 'flex', alignItems: 'center', flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = '#FEF2F2'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9CA3AF'; e.currentTarget.style.background = 'none'; }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
